import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';

/**
 * Enterprise-grade hook for Team Page translations with type safety and error handling
 * Provides centralized access to all Team-related translations with memoization
 */
export function useTeampageTranslations() {
  // Use the default translation namespace since teampage.json is merged into translation
  const { t, i18n } = useTranslation('translation');

  // Helper functions for common translation patterns with type safety
  const getMainText = (key: string, params?: Record<string, any>): string => {
    return String(t(`teampage.main.${key}`, params || {}));
  };

  const getTabsText = (key: string, params?: Record<string, any>): string => {
    return String(t(`teampage.tabs.${key}`, params || {}));
  };

  const getButtonsText = (key: string, params?: Record<string, any>): string => {
    return String(t(`teampage.buttons.${key}`, params || {}));
  };

  const getTableText = (key: string, params?: Record<string, any>): string => {
    return String(t(`teampage.table.${key}`, params || {}));
  };

  const getFiltersText = (key: string, params?: Record<string, any>): string => {
    return String(t(`teampage.filters.${key}`, params || {}));
  };

  const getStatusText = (key: string, params?: Record<string, any>): string => {
    return String(t(`teampage.status.${key}`, params || {}));
  };

  const getActivityText = (key: string, params?: Record<string, any>): string => {
    return String(t(`teampage.activity.${key}`, params || {}));
  };

  const getEmptyStateText = (key: string, params?: Record<string, any>): string => {
    return String(t(`teampage.emptyState.${key}`, params || {}));
  };

  const getPaginationText = (key: string, params?: Record<string, any>): string => {
    return String(t(`teampage.pagination.${key}`, params || {}));
  };

  const getErrorsText = (key: string, params?: Record<string, any>): string => {
    return String(t(`teampage.errors.${key}`, params || {}));
  };

  const getSuccessText = (key: string, params?: Record<string, any>): string => {
    return String(t(`teampage.success.${key}`, params || {}));
  };

  const getToastText = (key: string, params?: Record<string, any>): string => {
    return String(t(`teampage.toast.${key}`, params || {}));
  };

  const getModalsText = (section: string, key: string, params?: Record<string, any>): string => {
    return String(t(`teampage.modals.${section}.${key}`, params || {}));
  };

  const getManageControlsText = (key: string, params?: Record<string, any>): string => {
    return String(t(`teampage.manageControls.${key}`, params || {}));
  };

  const getHistoryText = (key: string, params?: Record<string, any>): string => {
    return String(t(`teampage.history.${key}`, params || {}));
  };

  const getRolesText = (roleName: string, key: string, params?: Record<string, any>): string => {
    return String(t(`teampage.roles.${roleName}.${key}`, params || {}));
  };

  // Safe translation wrapper that always returns string
  const tSafe = (key: string, params?: Record<string, any>): string => {
    return String(t(key, params || {}));
  };

  // Memoized complex helpers to prevent re-renders
  const getMainPageText = useMemo(
    () => ({
      pageTitle: getMainText('pageTitle'),
      pageDescription: getMainText('pageDescription'),
      loading: getMainText('loading'),
      searchPlaceholder: getMainText('searchPlaceholder'),
      searchAriaLabel: getMainText('searchAriaLabel'),
    }),
    [t]
  );

  const getTabLabels = useMemo(
    () => ({
      allMembers: getTabsText('allMembers'),
      active: getTabsText('active'),
      pending: getTabsText('pending'),
    }),
    [t]
  );

  const getButtonLabels = useMemo(
    () => ({
      history: getButtonsText('history'),
      bulkInvite: getButtonsText('bulkInvite'),
      inviteMember: getButtonsText('inviteMember'),
      resend: getButtonsText('resend'),
      cancel: getButtonsText('cancel'),
      retry: getButtonsText('retry'),
      save: getButtonsText('save'),
      saveChanges: getButtonsText('saveChanges'),
      saving: getButtonsText('saving'),
      first: getButtonsText('first'),
      previous: getButtonsText('previous'),
      next: getButtonsText('next'),
      last: getButtonsText('last'),
      refresh: getButtonsText('refresh'),
      returnToDashboard: getButtonsText('returnToDashboard'),
      tryAgain: getButtonsText('tryAgain'),
      backToTeam: getButtonsText('backToTeam'),
    }),
    [t]
  );

  const getTableHeaders = useMemo(
    () => ({
      member: getTableText('headers.member'),
      role: getTableText('headers.role'),
      lastAction: getTableText('headers.lastAction'),
      lastActivity: getTableText('headers.lastActivity'),
      actions: getTableText('headers.actions'),
    }),
    [t]
  );

  const getFilterLabels = useMemo(
    () => ({
      filterByRole: getFiltersText('filterByRole'),
      filterByRoleAriaLabel: getFiltersText('filterByRoleAriaLabel'),
      allRoles: getFiltersText('allRoles'),
    }),
    [t]
  );

  const getStatusLabels = useMemo(
    () => ({
      pending: getStatusText('pending'),
      active: getStatusText('active'),
      unknown: getStatusText('unknown'),
    }),
    [t]
  );

  const getActivityLabels = useMemo(
    () => ({
      noActionRecently: getActivityText('noActionRecently'),
      invitedAgo: (time: string) => getActivityText('invitedAgo', { time }),
      recentlyInvited: getActivityText('recentlyInvited'),
      lastActiveAgo: (time: string) => getActivityText('lastActiveAgo', { time }),
      activeRecently: getActivityText('activeRecently'),
      activityStatusUnknown: getActivityText('activityStatusUnknown'),
      unknownAction: getActivityText('unknownAction'),
      hoursAgo: (count: number) =>
        getActivityText('hoursAgo', { count, plural: count !== 1 ? 's' : '' }),
      minutesAgo: (count: number) =>
        getActivityText('minutesAgo', { count, plural: count !== 1 ? 's' : '' }),
      justNow: getActivityText('justNow'),
      daysAgo: (params: { count: number; plural: string }) => getActivityText('daysAgo', params),
      weeksAgo: (params: { count: number; plural: string }) => getActivityText('weeksAgo', params),
      monthsAgo: (params: { count: number; plural: string }) =>
        getActivityText('monthsAgo', params),
      yearsAgo: (params: { count: number; plural: string }) => getActivityText('yearsAgo', params),
      productCreated: (productName: string) => getActivityText('productCreated', { productName }),
      productUpdated: (productName: string) => getActivityText('productUpdated', { productName }),
      productDeleted: (productName: string) => getActivityText('productDeleted', { productName }),
      productArchived: (productName: string) => getActivityText('productArchived', { productName }),
      user: getActivityText('user'),
    }),
    [t]
  );

  const getEmptyStateLabels = useMemo(
    () => ({
      title: getEmptyStateText('title'),
      description: getEmptyStateText('description'),
    }),
    [t]
  );

  const getPaginationLabels = useMemo(
    () => ({
      showing: (start: number, end: number, total: number) =>
        getPaginationText('showing', { start, end, total }),
      page: (current: number, total: number) => getPaginationText('page', { current, total }),
    }),
    [t]
  );

  const getErrorLabels = useMemo(
    () => ({
      organizationError: getErrorsText('organizationError'),
      noOrganizationId: getErrorsText('noOrganizationId'),
      noAssociatedOrganization: getErrorsText('noAssociatedOrganization'),
      authenticationRequired: getErrorsText('authenticationRequired'),
      noOrganizationIdSupport: getErrorsText('noOrganizationIdSupport'),
      dataError: getErrorsText('dataError'),
      failedToLoadTeamData: getErrorsText('failedToLoadTeamData'),
      noOrganizationIdRoles: getErrorsText('noOrganizationIdRoles'),
      noOrganizationIdMemberships: getErrorsText('noOrganizationIdMemberships'),
      organizationIdMissing: getErrorsText('organizationIdMissing'),
      noPermissionRemove: getErrorsText('noPermissionRemove'),
      noPermissionInvite: getErrorsText('noPermissionInvite'),
      errorRemoving: (message: string) => getErrorsText('errorRemoving', { message }),
      errorResending: (message: string) => getErrorsText('errorResending', { message }),
      errorFormatting: getErrorsText('errorFormatting'),
      errorFormattingDate: getErrorsText('errorFormattingDate'),
      unableToLoadAuditLogs: getErrorsText('unableToLoadAuditLogs'),
      unknownErrorOccurred: getErrorsText('unknownErrorOccurred'),
    }),
    [t]
  );

  const getSuccessLabels = useMemo(
    () => ({
      avatarUpdated: getSuccessText('avatarUpdated'),
      inviteCanceled: getSuccessText('inviteCanceled'),
      memberRemoved: getSuccessText('memberRemoved'),
      invitationResent: getSuccessText('invitationResent'),
    }),
    [t]
  );

  const getToastLabels = useMemo(
    () => ({
      team: {
        memberInviteSuccess: getToastText('team.memberInviteSuccess'),
        memberInviteError: getToastText('team.memberInviteError'),
        memberRemoveSuccess: getToastText('team.memberRemoveSuccess'),
        memberRemoveError: getToastText('team.memberRemoveError'),
        roleChangeSuccess: getToastText('team.roleChangeSuccess'),
        roleChangeError: getToastText('team.roleChangeError'),
        inviteResendSuccess: getToastText('team.inviteResendSuccess'),
        inviteResendError: getToastText('team.inviteResendError'),
        inviteCancelSuccess: getToastText('team.inviteCancelSuccess'),
        inviteCancelError: getToastText('team.inviteCancelError'),
        bulkInviteSuccess: getToastText('team.bulkInviteSuccess'),
        bulkInviteError: getToastText('team.bulkInviteError'),
        loadMembersError: getToastText('team.loadMembersError'),
        loadRolesError: getToastText('team.loadRolesError'),
      },
      organization: {
        createSuccess: getToastText('organization.createSuccess'),
        updateSuccess: getToastText('organization.updateSuccess'),
        deleteSuccess: getToastText('organization.deleteSuccess'),
        createError: getToastText('organization.createError'),
        updateError: getToastText('organization.updateError'),
        deleteError: getToastText('organization.deleteError'),
      },
    }),
    [t]
  );

  const getInviteModalText = useMemo(
    () => ({
      title: getModalsText('invite', 'title'),
      description: getModalsText('invite', 'description'),
      emailLabel: getModalsText('invite', 'emailLabel'),
      emailPlaceholder: getModalsText('invite', 'emailPlaceholder'),
      emailHelp: getModalsText('invite', 'emailHelp'),
      roleLabel: getModalsText('invite', 'roleLabel'),
      rolePlaceholder: getModalsText('invite', 'rolePlaceholder'),
      emailRequired: getModalsText('invite', 'emailRequired'),
      selectRole: getModalsText('invite', 'selectRole'),
      invitationSent: (email: string) => getModalsText('invite', 'invitationSent', { email }),
      failedToSend: getModalsText('invite', 'failedToSend'),
      cancel: getModalsText('invite', 'cancel'),
      sendInvitation: getModalsText('invite', 'sendInvitation'),
      sending: getModalsText('invite', 'sending'),
    }),
    [t]
  );

  const getBulkInviteModalText = useMemo(
    () => ({
      title: getModalsText('bulkInvite', 'title'),
      description: getModalsText('bulkInvite', 'description'),
      emailsLabel: getModalsText('bulkInvite', 'emailsLabel'),
      emailsPlaceholder: getModalsText('bulkInvite', 'emailsPlaceholder'),
      emailsHelp: getModalsText('bulkInvite', 'emailsHelp'),
      roleLabel: getModalsText('bulkInvite', 'roleLabel'),
      rolePlaceholder: getModalsText('bulkInvite', 'rolePlaceholder'),
      sampleFormatButton: getModalsText('bulkInvite', 'sampleFormatButton'),
      sampleEmails: getModalsText('bulkInvite', 'sampleEmails'),
      validating: getModalsText('bulkInvite', 'validating'),
      emailsRequired: getModalsText('bulkInvite', 'emailsRequired'),
      selectRole: getModalsText('bulkInvite', 'selectRole'),
      validEmails: (count: number) =>
        getModalsText('bulkInvite', 'validEmails', {
          count,
          plural: count !== 1 ? 's' : '',
        }),
      invalidEmails: (count: number) =>
        getModalsText('bulkInvite', 'invalidEmails', {
          count,
          plural: count !== 1 ? 's' : '',
        }),
      invalidEmailFormat: getModalsText('bulkInvite', 'invalidEmailFormat'),
      invitationsSent: getModalsText('bulkInvite', 'invitationsSent'),
      invitationsFailed: getModalsText('bulkInvite', 'invitationsFailed'),
      endpointNotFound: getModalsText('bulkInvite', 'endpointNotFound'),
      notAuthorized: getModalsText('bulkInvite', 'notAuthorized'),
      invalidInput: getModalsText('bulkInvite', 'invalidInput'),
      partialFailure: (count: number) => getModalsText('bulkInvite', 'partialFailure', { count }),
      failedToInvite: getModalsText('bulkInvite', 'failedToInvite'),
      errorValidating: getModalsText('bulkInvite', 'errorValidating'),
      cancel: getModalsText('bulkInvite', 'cancel'),
      sendInvitations: getModalsText('bulkInvite', 'sendInvitations'),
      sending: getModalsText('bulkInvite', 'sending'),
    }),
    [t]
  );

  const getRoleChangeModalText = useMemo(
    () => ({
      title: getModalsText('roleChange', 'title'),
      description: getModalsText('roleChange', 'description'),
      pleaseSelectRole: getModalsText('roleChange', 'pleaseSelectRole'),
      roleUnchanged: getModalsText('roleChange', 'roleUnchanged'),
      roleUpdated: getModalsText('roleChange', 'roleUpdated'),
      failedToUpdate: (error: string) => getModalsText('roleChange', 'failedToUpdate', { error }),
      unknownError: getModalsText('roleChange', 'unknownError'),
      cancel: getModalsText('roleChange', 'cancel'),
      saveChanges: getModalsText('roleChange', 'saveChanges'),
      saving: getModalsText('roleChange', 'saving'),
    }),
    [t]
  );

  const getProfilePictureModalText = useMemo(
    () => ({
      title: {
        select: getModalsText('profilePicture', 'title.select'),
        crop: getModalsText('profilePicture', 'title.crop'),
        upload: getModalsText('profilePicture', 'title.upload'),
      },
      description: {
        select: getModalsText('profilePicture', 'description.select'),
        crop: getModalsText('profilePicture', 'description.crop'),
        upload: getModalsText('profilePicture', 'description.upload'),
      },
      buttons: {
        removePhoto: getModalsText('profilePicture', 'buttons.removePhoto'),
        uploadNewPhoto: getModalsText('profilePicture', 'buttons.uploadNewPhoto'),
        back: getModalsText('profilePicture', 'buttons.back'),
        applyCrop: getModalsText('profilePicture', 'buttons.applyCrop'),
        cancel: getModalsText('profilePicture', 'buttons.cancel'),
        removing: getModalsText('profilePicture', 'buttons.removing'),
      },
      cropInstructions: getModalsText('profilePicture', 'cropInstructions'),
      uploadProgress: (progress: number) =>
        getModalsText('profilePicture', 'uploadProgress', { progress }),
    }),
    [t]
  );

  const getManageControlsLabels = useMemo(
    () => ({
      changeRole: getManageControlsText('changeRole'),
      removeMember: getManageControlsText('removeMember'),
      cannotChangeOwnRole: getManageControlsText('cannotChangeOwnRole'),
      cannotRemoveYourself: getManageControlsText('cannotRemoveYourself'),
      cannotRemoveOrgOwner: getManageControlsText('cannotRemoveOrgOwner'),
    }),
    [t]
  );

  const getRoleLabels = useMemo(
    () => ({
      Admin: {
        name: getRolesText('Admin', 'name'),
        description: getRolesText('Admin', 'description'),
      },
      Editor: {
        name: getRolesText('Editor', 'name'),
        description: getRolesText('Editor', 'description'),
      },
      Viewer: {
        name: getRolesText('Viewer', 'name'),
        description: getRolesText('Viewer', 'description'),
      },
      OrgOwner: {
        name: getRolesText('OrgOwner', 'name'),
        description: getRolesText('OrgOwner', 'description'),
      },
    }),
    [t]
  );

  const getHistoryLabels = useMemo(
    () => ({
      pageTitle: getHistoryText('pageTitle'),
      pageDescription: getHistoryText('pageDescription'),
      noRecordsFound: getHistoryText('noRecordsFound'),
      recordsDescription: getHistoryText('recordsDescription'),
      actionsDescription: getHistoryText('actionsDescription'),
      searchPlaceholder: getHistoryText('searchPlaceholder'),
      filterByAction: getHistoryText('filterByAction'),
      filterByDate: getHistoryText('filterByDate'),
      filterDescription: getHistoryText('filterDescription'),
      allActions: getHistoryText('allActions'),
      anyTime: getHistoryText('anyTime'),
      lastWeek: getHistoryText('lastWeek'),
      lastMonth: getHistoryText('lastMonth'),
      lastQuarter: getHistoryText('lastQuarter'),
      applyFilters: getHistoryText('applyFilters'),
      resetFilters: getHistoryText('resetFilters'),
      exportData: getHistoryText('exportData'),
      sortBy: getHistoryText('sortBy'),
      dateTime: getHistoryText('dateTime'),
      timestamp: getHistoryText('timestamp'),
      action: getHistoryText('action'),
      user: getHistoryText('user'),
      ascending: getHistoryText('ascending'),
      descending: getHistoryText('descending'),
      viewDetails: getHistoryText('viewDetails'),
      closeDetails: getHistoryText('closeDetails'),
      detailsTitle: getHistoryText('detailsTitle'),
      detailsDescription: getHistoryText('detailsDescription'),
      performedBy: getHistoryText('performedBy'),
      actionType: getHistoryText('actionType'),
      affectedUser: getHistoryText('affectedUser'),
      additionalDetails: getHistoryText('additionalDetails'),
      eventInformation: getHistoryText('eventInformation'),
      target: getHistoryText('target'),
      showRawJson: getHistoryText('showRawJson'),
      system: getHistoryText('system'),
      unnamedUser: getHistoryText('unnamedUser'),
      today: getHistoryText('today'),
      yesterday: getHistoryText('yesterday'),
      entry: getHistoryText('entry'),
      entries: getHistoryText('entries'),
      details: {
        invitationDetails: getHistoryText('details.invitationDetails'),
        roleChangeDetails: getHistoryText('details.roleChangeDetails'),
        removalDetails: getHistoryText('details.removalDetails'),
        eventDetails: getHistoryText('details.eventDetails'),
        email: getHistoryText('details.email'),
        role: getHistoryText('details.role'),
        message: getHistoryText('details.message'),
        from: getHistoryText('details.from'),
        to: getHistoryText('details.to'),
        reason: getHistoryText('details.reason'),
      },
      actions: {
        invite: getHistoryText('actions.invite'),
        role_change: getHistoryText('actions.role_change'),
        remove: getHistoryText('actions.remove'),
        inviteDescription: (email: string, role: string) =>
          getHistoryText('actions.inviteDescription', { email, role }),
        roleChangeDescription: (email: string, from: string, to: string) =>
          getHistoryText('actions.roleChangeDescription', { email, from, to }),
        removeDescription: (email: string, role: string) =>
          getHistoryText('actions.removeDescription', { email, role }),
        defaultDescription: (action: string) =>
          getHistoryText('actions.defaultDescription', { action }),
      },
      pagination: {
        showing: (start: number, end: number, total: number) =>
          getHistoryText('pagination.showing', { start, end, total }),
        page: (current: number, total: number) =>
          getHistoryText('pagination.page', { current, total }),
        noResults: getHistoryText('pagination.noResults'),
      },
    }),
    [t]
  );

  return {
    // Direct translation function with type safety
    t: tSafe,
    i18n,

    // Category-specific helpers
    getMainText,
    getTabsText,
    getButtonsText,
    getTableText,
    getFiltersText,
    getStatusText,
    getActivityText,
    getEmptyStateText,
    getPaginationText,
    getErrorsText,
    getSuccessText,
    getToastText,
    getModalsText,
    getManageControlsText,
    getHistoryText,
    getRolesText,

    // Memoized complex helpers to prevent re-renders
    mainPage: getMainPageText,
    tabs: getTabLabels,
    buttons: getButtonLabels,
    table: getTableHeaders,
    filters: getFilterLabels,
    status: getStatusLabels,
    activity: getActivityLabels,
    emptyState: getEmptyStateLabels,
    pagination: getPaginationLabels,
    errors: getErrorLabels,
    success: getSuccessLabels,
    toast: getToastLabels,
    inviteModal: getInviteModalText,
    bulkInviteModal: getBulkInviteModalText,
    roleChangeModal: getRoleChangeModalText,
    profilePictureModal: getProfilePictureModalText,
    manageControls: getManageControlsLabels,
    roles: getRoleLabels,
    history: getHistoryLabels,
  };
}

export type TeampageTranslations = ReturnType<typeof useTeampageTranslations>;
